import { Badge, SvgIcon } from '@salecycle/ui-component-library';
import { useCallback, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { WorkflowBox } from '../../WorkflowBox/WorkflowBox';
import { WorkflowBoxHeader } from '../../WorkflowBox/WorkflowBoxHeader';
import { useWorkflowForm } from '../../../contexts/CampaignWorkflowFormContext';
import { useDrawer } from '../../../state/Drawer/hooks/useDrawer';
import { DisplayWebContent } from '../DisplayWebContent/components/DisplayWebContent';
import { FormKeys } from '../../../state/CampaignWorkflowFormActions';
import useOpenDrawerSettings from '../DisplayWebContent/hooks/useOpenDrawerSettings';
import { useTranslation } from 'react-i18next';
import { Variant } from '../../../state/SplitTests/SplitTestsState';
import { useVariantsSelector } from '../../../state/SplitTests/selectors/useVariantsSelector';
import { RemoveSplitTestModal } from './components/RemoveSplitTestModal';
import { ControlGroupInfo } from '../DisplayWebContent/components/ControlGroupInfo';
import { useIsSplitTestControlGroupSelector } from '../../../state/SplitTests/selectors/useIsSplitTestControlGroupSelector';
import { useSplitTestControlGroupWeightSelector } from '../../../state/SplitTests/selectors/useSplitTestControlGroupWeightSelector';
import { useSchedulingSelector } from '../../../state/SplitTests/selectors/useSchedulingSelector';
import { useShowValidationWarning } from './hooks/useShowValidationWarning';
import { WorkflowContent } from '../../WorkflowBox/WorkflowContent';
import { useSplitTestIdSelector } from '../../../state/SplitTests/selectors/useSplitTestIdSelector';
import { useSplitTestNameSelector } from '../../../state/SplitTests/selectors/useSplitTestNameSelector';

const VariantContainer = styled.div`
  display: flex;
  > div:first-child {
    margin-right: ${({ theme }) => theme.designToken.spacing3};
  }
`;

const DatesContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.designToken.spacing3};
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.designToken.spacing4};
`;

const ContentInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.designToken.spacing3};
`;

const ScheduledDates = ({ splitTestId }: { splitTestId: string }) => {
  const { t } = useTranslation('campaigns');
  const { startDate, startTime, endDate, endTime } = useSchedulingSelector({ splitTestId });

  const theme = useTheme();

  return (
    <DatesContainer>
      {startDate && (
        <Badge
          icon="calendar-days"
          useSvgIcon
          displayIconLeft
          rounded={4}
          size="small"
          color={theme.designToken.neutral50}
          iconColor={theme.designToken.neutral700}
          style={{ color: theme.designToken.neutral900, font: theme.designToken.body2Semibold }}
        >
          {t('onsite.editor.workflow.displayWebContentMessage.scheduling.startDateBadge', {
            startDate: startDate.toLocaleDateString('en-GB'),
            startTime,
          })}
        </Badge>
      )}
      {endDate && (
        <Badge
          icon="calendar-days"
          useSvgIcon
          displayIconLeft
          rounded={4}
          size="small"
          color={theme.designToken.neutral50}
          iconColor={theme.designToken.neutral700}
          style={{ color: theme.designToken.neutral900, font: theme.designToken.body2Semibold }}
        >
          {t('onsite.editor.workflow.displayWebContentMessage.scheduling.endDateBadge', {
            endDate: endDate.toLocaleDateString('en-GB'),
            endTime,
          })}
        </Badge>
      )}
    </DatesContainer>
  );
};

export function SplitTestSettingsWorkflow({ id }: { id: string }) {
  const { t } = useTranslation('campaigns');
  const { actions } = useWorkflowForm();

  const { closeDrawer } = useDrawer();
  const { openDrawerSettings } = useOpenDrawerSettings();
  const [openModal, setOpenModal] = useState(false);

  const removeWorkflow = useCallback(() => {
    setOpenModal(true);
    closeDrawer();
    actions.setFocused(id, false);
    actions.setTouched(id, false);
  }, [actions, closeDrawer, setOpenModal, id]);

  const handleOpenConfiguration = useCallback(() => openDrawerSettings(id, 'split-test-settings'), [
    id,
    openDrawerSettings,
  ]);

  const onClose = useCallback(() => setOpenModal(false), []);

  const showValidationWarning = useShowValidationWarning({ splitTestId: id });
  const variants = useVariantsSelector(id);
  const ControlGroup = useIsSplitTestControlGroupSelector(id);
  const ControlGroupWeight = useSplitTestControlGroupWeightSelector(id);
  const splitTestId = useSplitTestIdSelector(id);
  const name = useSplitTestNameSelector(id);

  return (
    <>
      <WorkflowBox
        variant={showValidationWarning ? 'Incomplete' : 'Default'}
        id={id}
        ariaLabel={t('onsite.editor.workflow.splitTest.workFlowBoxAriaLabel')}
        isExtraSize={true}
      >
        <WorkflowBoxHeader
          id={splitTestId}
          icon={<SvgIcon name="vials" size="32" />}
          title={t('onsite.editor.workflow.splitTest.workFlowBoxTitle')}
          onOpenConfiguration={handleOpenConfiguration}
          onRemove={removeWorkflow}
          onRemoveAriaLable={t('onsite.editor.workflow.splitTest.workFlowModalRemoveSplitTest')}
          actionsMenuAriaLabel={t('onsite.editor.workflow.splitTest.workFlowBoxEditAriaLabel')}
          variants={variants}
          onEditVariant={openDrawerSettings}
          name={name}
          nameAriaLabel={t('onsite.editor.workflow.splitTest.workflowNameAria')}
        />
        <ContentContainer>
          <WorkflowContent
            showValidationWarning={showValidationWarning}
            warningPosition="bottom"
            warningMessage={t('onsite.editor.workflow.splitTest.workflowMissingSettingsWarning')}
            alertAriaLabel={t('onsite.editor.workflow.splitTest.workflowMissingSettingsWarning')}
          >
            <ContentInfoContainer>
              <ScheduledDates splitTestId={id} />
              {ControlGroup && <ControlGroupInfo ControlGroupWeight={ControlGroupWeight} isSplitTest={true} />}
            </ContentInfoContainer>
          </WorkflowContent>
          <VariantContainer>
            {variants?.map((variant: Variant) => (
              <DisplayWebContent key={variant.messageId} id={variant.messageId as FormKeys} isVariant />
            ))}
          </VariantContainer>
        </ContentContainer>
      </WorkflowBox>

      <RemoveSplitTestModal isOpen={openModal} onClose={onClose} splitTestId={id} />
    </>
  );
}
